import { jsx, jsxs } from 'react/jsx-runtime';
import * as React from 'react';
import { throttle } from 'throttle-debounce';
import { c as classNames } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-tabs.css';
import { v as validatePropType } from '../validate-prop-types/index-chunk.esm.js';

const getClassNameList$3 = classNames.bind(styles);
class EGDSTab extends React.Component {
    static displayName = 'EGDSTab';
    parentDiv = React.createRef();
    render() {
        const { selected } = this.props;
        const wrapperClassNames = getClassNameList$3('uitk-tabs-pane', {
            [`active`]: selected,
        });
        const { name, children, id } = this.props;
        return (jsx("div", { ref: this.parentDiv, role: "tabpanel", className: wrapperClassNames, id: id, children: children }, name));
    }
}

const getClassNameList$2 = classNames.bind(styles);
const EGDSTabHighlighter = (props) => {
    const { className, style } = props;
    return jsx("li", { "aria-hidden": "true", className: getClassNameList$2('uitk-tab-highlighter', className), style: style });
};
EGDSTabHighlighter.displayName = 'EGDSTabHighlighter';

const getClassNameList$1 = classNames.bind(styles);
class EGDSTabAnchor extends React.Component {
    static displayName = 'EGDSTabAnchor';
    selectedLiRef;
    constructor(props) {
        super(props);
        this.selectedLiRef = React.createRef();
        this.preventClickEvent = this.preventClickEvent.bind(this);
        this.handleOnClick = this.handleOnClick.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }
    handleOnClick(event) {
        const { disabled, handleClick, index, targetRef } = this.props;
        this.preventClickEvent(event);
        if (disabled) {
            return false;
        }
        if (typeof handleClick === 'function') {
            handleClick(index, event, targetRef);
        }
    }
    handleFocus(event) {
        const { handleFocus } = this.props;
        if (typeof handleFocus === 'function') {
            handleFocus(event);
        }
    }
    handleBlur(event) {
        const { handleBlur } = this.props;
        if (typeof handleBlur === 'function') {
            handleBlur(event);
        }
    }
    preventClickEvent(e) {
        const { externalLinks } = this.props;
        if (!externalLinks) {
            e.preventDefault();
        }
    }
    render() {
        const { className, disabled, name, targetURI, panelId, navigationType, selected, tabLinkAttr, anchorTabRef, } = this.props;
        const wrapperClassNames = getClassNameList$1('uitk-tab', className, {
            [`active`]: selected,
        });
        const anchorClassNames = getClassNameList$1('uitk-tab-anchor', {
            [`uitk-tab-anchor-selected`]: selected,
            [`uitk-tab-anchor-disabled`]: disabled,
        });
        const isJumplink = navigationType === EGDSNavigationType.JUMPLINK;
        return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        jsx("li", { "data-stid": selected ? 'uitk-tab-active' : null, role: isJumplink ? 'listitem' : 'presentation', className: wrapperClassNames, onClick: this.preventClickEvent, ref: selected ? this.selectedLiRef : '', children: jsx("a", { href: targetURI, ref: anchorTabRef, className: anchorClassNames, ...(!isJumplink && { 'aria-controls': panelId }), ...(isJumplink ? { 'aria-current': selected } : { 'aria-selected': selected }), ...tabLinkAttr, role: isJumplink ? 'link' : 'tab', "data-toggle": "tab", onClick: this.handleOnClick, onFocus: this.handleFocus, onBlur: this.handleBlur, draggable: false, children: jsx("span", { className: getClassNameList$1('uitk-tab-text'), children: name }) }) }));
    }
}

const getClassNameList = classNames.bind(styles);
const DEFAULT_TAB_HEIGHT = 48; // 3x default font height (16px)
var EGDSTabsType;
(function (EGDSTabsType) {
    EGDSTabsType["EQUAL_WIDTH"] = "equal-width";
    EGDSTabsType["NATURAL"] = "natural";
})(EGDSTabsType || (EGDSTabsType = {}));
var EGDSTabsBackGroundColorType;
(function (EGDSTabsBackGroundColorType) {
    EGDSTabsBackGroundColorType["PRIMARY"] = "background-primary";
    EGDSTabsBackGroundColorType["SECONDARY"] = "background-secondary";
})(EGDSTabsBackGroundColorType || (EGDSTabsBackGroundColorType = {}));
var EGDSNavigationType;
(function (EGDSNavigationType) {
    EGDSNavigationType["DEFAULT"] = "default";
    EGDSNavigationType["JUMPLINK"] = "jumplink";
})(EGDSNavigationType || (EGDSNavigationType = {}));
class EGDSTabs extends React.Component {
    static displayName = 'EGDSTabs';
    static defaultProps = {
        backgroundColor: EGDSTabsBackGroundColorType.PRIMARY,
        centerAlign: false,
        navigationType: EGDSNavigationType.DEFAULT,
        tabsType: EGDSTabsType.EQUAL_WIDTH,
        withBorder: true,
    };
    static baseClassName = 'uitk-tabs';
    tabsRef;
    // SelectedIndex is the index of the htmlElement we want to focus on in an array of htmlElements
    selectedIndex;
    selectedTabOffset;
    transitionTimeout;
    throttleDelay = 100;
    animationTimer = 275;
    listenersAdded = false;
    intersectionObserver;
    idsCollection = [];
    scrollMouse = { drag: false, down: false, initialMousePos: 0, scrollLeft: 0 };
    constructor(props) {
        super(props);
        this.state = {
            isTransitioning: false,
            mounted: false,
        };
        this.tabsRef = React.createRef();
        this.handleClick = this.handleClick.bind(this);
        this.centerTab = this.centerTab.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.setSelectedElementOffset = this.setSelectedElementOffset.bind(this);
        this.setSelectedTab = this.setSelectedTab.bind(this);
        this.observeTabs = this.observeTabs.bind(this);
        this.unobserveTabs = this.unobserveTabs.bind(this);
        this.updateHighlighter = throttle(this.throttleDelay, this.updateHighlighter.bind(this), {
            debounceMode: false,
        });
        this.tabSelected = this.tabSelected.bind(this);
        this.mouseDown = this.mouseDown.bind(this);
        this.mouseMove = this.mouseMove.bind(this);
        this.mouseUp = this.mouseUp.bind(this);
    }
    componentDidMount() {
        const { navigationType } = this.props;
        if (this.tabsRef) {
            this.updateHighlighter();
        }
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.updateHighlighter);
        }
        if (this.idsCollection.length > 0 && navigationType === EGDSNavigationType.JUMPLINK) {
            const topOfIntersectionObserver = this.tabsRef.current ? this.tabsRef?.current?.offsetHeight : DEFAULT_TAB_HEIGHT;
            const threePercentOfRestOfPage = (window.innerHeight - topOfIntersectionObserver) * 0.03;
            const bottomOfIntersectionObserver = window.innerHeight - (topOfIntersectionObserver + threePercentOfRestOfPage);
            const options = {
                rootMargin: `-${topOfIntersectionObserver}px 0px -${bottomOfIntersectionObserver}px`,
                threshold: 0,
            };
            this.intersectionObserver = new IntersectionObserver(this.setSelectedTab, options);
            this.observeTabs();
        }
        this.setState({
            mounted: true,
        });
    }
    componentDidUpdate(prevProps) {
        const { selectedTab } = this.props;
        if (selectedTab !== prevProps.selectedTab) {
            // When the activeIndex or the links are updated, transition
            // the highlighter to the active tab.
            this.updateHighlighter();
        }
    }
    componentWillUnmount() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('resize', this.updateHighlighter);
        }
        if (this.transitionTimeout) {
            clearTimeout(this.transitionTimeout);
            this.transitionTimeout = null;
        }
        if (this.listenersAdded) {
            document.body.removeEventListener('keyup', this.onKeyUp);
            document.body.removeEventListener('keydown', this.onKeyDown);
            this.listenersAdded = false;
        }
    }
    handleBlur() {
        if (this.listenersAdded) {
            document.body.removeEventListener('keyup', this.onKeyUp);
            document.body.removeEventListener('keydown', this.onKeyDown);
            this.listenersAdded = false;
        }
    }
    handleFocus(event) {
        if (!this.listenersAdded) {
            document.body.addEventListener('keyup', this.onKeyUp);
            document.body.addEventListener('keydown', this.onKeyDown);
            this.listenersAdded = true;
        }
        this.centerTab(event);
    }
    handleClick(selectedTab, event, targetRef) {
        // Prevent click while dragging tabs
        if (this.scrollMouse.drag) {
            event.preventDefault();
            return;
        }
        const { navigationType, onTabClickCB } = this.props;
        this.tabSelected(selectedTab, event);
        if (typeof onTabClickCB === 'function') {
            onTabClickCB(selectedTab);
        }
        if (navigationType === EGDSNavigationType.JUMPLINK && selectedTab >= 0) {
            if (targetRef && targetRef.current) {
                const { scrollOffset, scrollOffsetSelf } = this.props;
                const scroll = window.scrollY;
                const target = targetRef.current.getBoundingClientRect().top;
                const offset = (scrollOffset || 0) +
                    (scrollOffsetSelf && !!this.tabsRef.current ? this.tabsRef.current.getBoundingClientRect().height : 0);
                window.scrollTo({
                    behavior: 'smooth',
                    top: scroll + target - offset,
                });
                const firstBaseChild = this.findBaseChild(targetRef.current);
                setTimeout(() => {
                    this.observeTabs();
                    firstBaseChild.focus();
                }, 1000);
            }
        }
    }
    onKeyDown(e) {
        const { navigationType } = this.props;
        if (e.key === 'ArrowRight' ||
            e.key === 'ArrowLeft' ||
            (e.key === ' ' && navigationType !== EGDSNavigationType.JUMPLINK)) {
            e.preventDefault();
        }
    }
    onKeyUp(e) {
        const { navigationType } = this.props;
        if (this.tabsRef.current) {
            const ul = this.tabsRef.current;
            const listItems = ul.children;
            let elemToFocus;
            let index = 0;
            if (e.key === 'ArrowRight') {
                index = this.selectedIndex + 1;
                elemToFocus = listItems[index];
            }
            else if (e.key === 'ArrowLeft') {
                index = this.selectedIndex - 1;
                elemToFocus = listItems[index];
            }
            else if (e.key === 'Tab') {
                const target = document.activeElement;
                for (let i = 0; i <= listItems.length; i++) {
                    if (target?.parentElement === listItems[i]) {
                        this.selectedIndex = i;
                    }
                }
                return;
            }
            else if (e.key === ' ') {
                if (navigationType !== EGDSNavigationType.JUMPLINK) {
                    this.tabSelected(this.selectedIndex, e);
                }
            }
            if (elemToFocus?.classList.contains('uitk-tab')) {
                this.selectedIndex = index;
                elemToFocus.children[0].focus();
            }
        }
    }
    getChildren() {
        const { children, externalLinks, navigationType, selectedTab } = this.props;
        const randomBase = `tab-${Math.random().toString(36).substr(2, 9)}-`;
        let navigationItems = [];
        let tabPaneItems = [];
        this.idsCollection = [];
        React.Children.forEach(children, (child, index) => {
            const { name, className, disabled, id, tabLinkAttr, targetRef, anchorTabRef, targetURI } = child.props;
            const selected = index === selectedTab;
            const panelId = id || `${randomBase}${index}`;
            if (selected) {
                this.selectedIndex = index;
            }
            // TargetURI is what gets assigned to the href attribute of the anchor tag inside a UITK tab
            // So if our navigation type is jumplink and the child has a targetURI prop defined,
            // Push that targetURI's into idsCollection. The ID we push is the htmlId of the section
            // that we're jumping to: 'targetURI.slice(targetURI.indexOf('#'))'
            if (navigationType === EGDSNavigationType.JUMPLINK && targetURI) {
                this.idsCollection.push(targetURI.slice(targetURI.indexOf('#')));
            }
            navigationItems = [
                // @ts-ignore
                ...navigationItems,
                jsx(EGDSTabAnchor, { disabled: disabled, className: className, handleClick: this.handleClick, handleFocus: this.handleFocus, handleBlur: this.handleBlur, index: index, name: name, selected: selected, targetRef: targetRef, anchorTabRef: anchorTabRef, targetURI: targetURI, panelId: panelId, externalLinks: externalLinks, navigationType: navigationType, tabLinkAttr: tabLinkAttr }, index),
            ];
            // If the current child component does not have children of its own, leave tabPaneItems as is
            // Else, if it does, create a new tabPaneItems array with the old content as well as a new UITKTab
            tabPaneItems = child.props.children
                ? [
                    ...tabPaneItems,
                    jsx(EGDSTab, { name: name, selected: selected, targetURI: targetURI, id: panelId, children: child.props.children }, index),
                ]
                : tabPaneItems;
        });
        // Return a 2d array where the first array is an array of UITKTabAnchors and the second is an array of
        // UITKTabs.
        return [navigationItems, tabPaneItems];
    }
    setSelectedElementOffset(selectedTab) {
        const isRtl = document.querySelector('html').getAttribute('dir') === 'rtl';
        const offsetRight = -(selectedTab.offsetParent?.clientWidth ?? 0 - selectedTab.offsetLeft - selectedTab.offsetWidth);
        this.selectedTabOffset = isRtl ? offsetRight : selectedTab.offsetLeft;
    }
    // This function is called whenever even one pixel of an observed item interesects the sliver of the viewport we are observing
    setSelectedTab(entries) {
        const { onTabSelect } = this.props;
        // This const is an array of section's id's that the tab will jump to
        const refs = this.idsCollection;
        let href = '';
        // For each section that has triggered a change, i.e. entered or left the sliver of the screen we're observing, do the following.
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                href = `#${entry.target.getAttribute('id')}`;
                this.selectedIndex = refs.indexOf(href);
                // In the case of jumplink tabs, this callback would cause the highlighter to switch to the active tab
                if (typeof onTabSelect === 'function') {
                    onTabSelect(this.selectedIndex);
                }
            }
        });
    }
    mouseDown = (ev) => {
        this.scrollMouse.down = true;
        this.scrollMouse.initialMousePos = ev.clientX;
        this.scrollMouse.scrollLeft = ev.currentTarget.scrollLeft;
    };
    mouseUp = (ev) => {
        this.scrollMouse.down = false;
        // Calculating if the mouse drags accidentally with a range of 10 pixels both to left and right.
        const hasDragToRight = this.scrollMouse.initialMousePos + 10 > ev.clientX;
        const hasDragToLeft = this.scrollMouse.initialMousePos - 10 < ev.clientX;
        // If it drags, then use the timeout to prevent click, if not, disabling drag.
        if (!hasDragToRight || !hasDragToLeft) {
            // Timeout drag flag in order to prevent click event
            setTimeout(() => {
                this.scrollMouse.drag = false;
            }, 0);
        }
        else {
            this.scrollMouse.drag = false;
        }
    };
    mouseMove = (ev) => {
        // Call only while dragging (mouseDown + mouseMove = drag)
        if (this.scrollMouse.down) {
            this.scrollMouse.drag = true;
            this.tabScroll(ev);
        }
    };
    updateHighlighter = () => {
        this.setState({
            isTransitioning: true,
        });
        this.transitionTimeout = setTimeout(() => {
            this.setState({
                isTransitioning: false,
            });
        }, this.animationTimer);
    };
    tabSelected(selectedTab, event) {
        const { onTabSelect } = this.props;
        this.selectedIndex = selectedTab;
        this.unobserveTabs();
        if (typeof onTabSelect === 'function') {
            onTabSelect(this.selectedIndex, event);
        }
        this.centerTab(event);
    }
    findBaseChild(obj) {
        let elem = obj;
        while (elem.childElementCount > 0) {
            elem = elem.firstElementChild;
        }
        return elem;
    }
    // Observe each of the html element defined with a corresponding id in idsCollection.
    // This will allow intersectionObserver to determine whether or not they intersect with the
    // viewport. This method acts on the DOM.
    observeTabs() {
        this.idsCollection.forEach((id) => {
            const elem = document.querySelector(id);
            if (elem) {
                this.intersectionObserver.observe(elem);
            }
        });
    }
    unobserveTabs() {
        this.idsCollection.forEach((id) => {
            const elem = document.querySelector(id);
            if (elem) {
                this.intersectionObserver.unobserve(elem);
            }
        });
    }
    centerTab(tabEl) {
        const { tabsType } = this.props;
        if (tabsType === EGDSTabsType.NATURAL) {
            tabEl.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }
    tabScroll(ev) {
        // How far the mouse has been moved
        const dx = ev.clientX - this.scrollMouse.initialMousePos;
        // Scroll the element
        if (this.tabsRef.current) {
            this.tabsRef.current.scrollLeft = this.scrollMouse.scrollLeft - dx;
        }
    }
    render() {
        const [navigationItems, tabPaneItems] = this.getChildren();
        const { ariaLabel, backgroundColor, className, centerAlign, navigationType, tabsType, withBorder } = this.props;
        const { isTransitioning, mounted } = this.state;
        const ulClassNames = getClassNameList(EGDSTabs.baseClassName, {
            [`${EGDSTabs.baseClassName}-${tabsType}`]: validatePropType(tabsType, EGDSTabsType),
        }, {
            [`${backgroundColor}`]: validatePropType(backgroundColor, EGDSTabsBackGroundColorType),
        }, {
            [`${EGDSTabs.baseClassName}-${navigationType}`]: validatePropType(navigationType, EGDSNavigationType),
        }, {
            [`${EGDSTabs.baseClassName}-with-border`]: withBorder,
        }, {
            [`${EGDSTabs.baseClassName}-unmounted`]: !mounted,
        }, {
            [`${EGDSTabs.baseClassName}-natural-center-align`]: centerAlign,
        }, className);
        let highlightStyle;
        // If our unordered list has children elements i.e. list items
        if (this.tabsRef?.current?.children) {
            const ul = this.tabsRef.current;
            const listItems = ul.children[this.selectedIndex || 0];
            // Grab the list item's anchor tag
            const selectedTab = listItems.firstElementChild;
            if (selectedTab) {
                this.setSelectedElementOffset(selectedTab);
                // Define the style and position of the highlighter element
                const highlighterWidth = selectedTab.getBoundingClientRect()?.width;
                const transformString = `translate${'X'}(${this.selectedTabOffset}px) scale${'Y'}(${isTransitioning ? '2' : '1'}) scaleX(1) `;
                highlightStyle = {
                    [`--uitk-tab-hl-block`]: '',
                    [`--uitk-tab-hl-transform`]: transformString,
                    [`--uitk-tab-hl-inline`]: highlighterWidth ? `${highlighterWidth}px` : '',
                };
            }
        }
        return (jsxs("div", { className: getClassNameList('uitk-tabs-container'), children: [jsxs("ul", { ref: this.tabsRef, className: ulClassNames, role: navigationType === EGDSNavigationType.JUMPLINK ? 'list' : 'tablist', onMouseDown: this.mouseDown, onMouseUp: this.mouseUp, onMouseMove: this.mouseMove, onMouseLeave: this.mouseUp, ...(ariaLabel ? { 'aria-label': ariaLabel } : {}), children: [navigationItems, jsx(EGDSTabHighlighter, { style: highlightStyle, className: isTransitioning ? 'uitk-tab-highlighter-is-transitioning' : '' })] }), navigationType === EGDSNavigationType.DEFAULT && tabPaneItems.length ? (jsx("div", { className: getClassNameList('uitk-tabs-content'), children: tabPaneItems })) : null] }));
    }
}

export { EGDSTab as E, EGDSTabHighlighter as a, EGDSTabAnchor as b, EGDSTabsType as c, EGDSTabsBackGroundColorType as d, EGDSNavigationType as e, EGDSTabs as f };
//# sourceMappingURL=index-chunk.esm.js.map
